<template>
  <div>
    <v-row v-if="isLoading">
      <v-col cols="12" class="vh-center">
        <v-progress-circular indeterminate :size="80" :width="2"></v-progress-circular>
      </v-col>
    </v-row>
    <v-form ref="memberForm" v-else>
      <v-row cols="12" md="12" lg="12" xl="12" class="text-left mt-4">
        <h2>{{ $t('member:member') }}</h2>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field v-model="member.firstname" :label="$t('member:firstname')" :rules="[Rules.required]"
            required></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="member.lastname" :label="$t('member:lastname')" :rules="[Rules.required]"
            required></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-text-field v-model="member.email" :label="$t('member:email')" :rules="[Rules.required, Rules.email]"
            required prepend-icon="email"></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field :rules="[Rules.required]" v-model="member.phone1" :label="$t('member:phone1')"
            prepend-icon="phone" class="bigger-hint" :hint="$t('member:smsReminderHint')"
            persistent-hint></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="member.phone2" :label="$t('member:phone2')" prepend-icon="phone"></v-text-field>
        </v-col>
        <v-col cols="12" v-if="$store.state.user && $store.state.user.isAdmin">
          <v-select v-model="member.status" :items="status" multiple chips
            label="Permissions" hint="Pour un administrateur, mettre 'member' et 'admin'" persistent-hint>
          </v-select>
        </v-col>
      </v-row>
      <v-row cols="12" md="12" lg="12" xl="12" class="text-left" v-if="isCreateFlow">
        <v-col>
          <v-text-field v-model="member.password" :label="$t('member:password')"
            :type="showPassword ? 'text' : 'password'" prepend-icon="lock" class="bigger-hint" persistent-hint
            :rules="[Rules.min8Char, Rules.required]" :append-icon="showPassword ? 'visibility' : 'visibility_off'"
            @click:append="showPassword = !showPassword"></v-text-field>
        </v-col>
      </v-row>
      <v-row cols="12" md="12" lg="12" xl="12" class="text-left">
        <h2>{{ $t('member:Facturation') }}</h2>
      </v-row>

      <v-row cols="12" md="12" lg="12" xl="12" class="text-left">
        <v-col>
          <v-text-field v-model="member.address_street" :label="$t('member:address_street')"></v-text-field>
        </v-col>
      </v-row>

      <v-row class="text-left">
        <v-col cols="12" sm="6">
          <v-text-field v-model="member.address_city" :label="$t('member:address_city')"></v-text-field>
        </v-col>
        <v-col cols="12" sm="3">
          <v-select v-model="member.address_province" :label="$t('member:address_province')" :items="provinces"
            item-value="value" :item-title="getProvinceText"></v-select>
        </v-col>
        <v-col>
          <v-text-field v-model="member.address_postalcode" :label="$t('member:address_postalcode')"></v-text-field>
        </v-col>
      </v-row>

      <v-row cols="12" md="12" lg="12" xl="12" class="text-left">
        <h2>
          {{ $t('member:Preferences') }}
        </h2>
      </v-row>
      <v-row class="text-left">
        <v-col cols="12" sm="6">
          <v-container fluid>
            <v-radio-group v-model="member.locale" inline class="pa-0" :rules="[Rules.required]">
              <v-radio label="Français" value="fr" class="pa-0"></v-radio>
              <v-radio label="English" value="en" class="pa-0"></v-radio>
            </v-radio-group>
          </v-container>
        </v-col>
        <v-col cols="12" sm="6">
          <v-select v-model="pickupLocationId" :label="$t('member:pickupLocation')" :items=pickupLocations
            item-value="id" item-title="name" :rules="[Rules.required]"></v-select>
        </v-col>
        <v-col cols="12">
          <h3>
            {{ $t('member:notifications') }}
          </h3>
          <v-row class="mt-2">
            <v-col cols="12" md="6">
              <h4>
                {{ $t('member:bySMS') }}
              </h4>
              <v-switch v-model="member.sms_preference" color="primary" prepend-icon="sms"
                :label="$t('member:onDeliveryDay')" value="beforeDistribution" hide-details></v-switch>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row class="mt-8">
        <v-col cols="6" class="text-left">
          <v-btn color="primary" class="mr-4 pull-left" @click="submit" size="large" :loading="isSubmitLoading"
            :disabled="isSubmitLoading">
            {{ buttonText }}
          </v-btn>
        </v-col>
        <v-col cols="6" class="text-right">
          <v-btn @click="$emit('close')" variant="text" v-if="showCloseButton" class="pull-right">
            {{ $t('close') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-snackbar v-model="formInvalidSnackbar" color="error" location="top" :timeout="7000">
      <span class="text-body-1 font-weight-bold">
        {{ $t('member:formInvalid') }}
      </span>
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="formInvalidSnackbar = false">
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Provinces from "@/Provinces";
import Rules from "@/Rules";
import I18n from "@/i18n";

export default {
  name: "MemberInfo",
  props: {
    buttonText: {
      type: String,
      default: ''
    },
    showCloseButton: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    Provinces.setupTranslation();
    I18n.i18next.addResources("fr", "member", {
      language: "Préférence",
      email: 'Courriel',
      password: 'Mot de passe',
      member: 'Membre',
      firstname: 'Prénom',
      lastname: 'Nom de Famille',
      phone1: 'Téléphone',
      smsReminderHint: "C'est ce numéro qui sera utilisé pour reçevoir des textos pour les rappels de livraison etc.",
      phone2: 'Téléphone secondaire',
      Facturation: 'Facturation',
      address_street: 'Adresse',
      address_city: 'Ville',
      address_province: 'Province',
      address_postalcode: 'Code postal',
      Preferences: 'Préférences',
      PointDeChute: 'Point De Chute',
      pickupLocation: 'Point De Chute',
      notifications: "Notifications",
      bySMS: 'Texto',
      byEmail: 'Courriel',
      onDeliveryDay: "Au jour de livraison",
      subscribedToEmails: "Reçevoir des courriels d'information sur les commande",
      formInvalid: "Le formulaire contient des erreurs. Veuillez les corriger avant de sauvegarder.",
    });
    I18n.i18next.addResources("en", "member", {
      language: "Preference",
      email: 'Email',
      password: 'Password',
      member: 'Member',
      firstname: 'First name',
      lastname: 'Last name',
      phone1: 'Phone',
      smsReminderHint: "This is the number that will be used to receive text messages for delivery reminders etc.",
      phone2: 'Secondary phone',
      Facturation: 'Invoicing',
      address_street: 'Address',
      address_city: 'City',
      address_province: 'Province',
      address_postalcode: 'Postal code',
      Preferences: 'Preferences',
      pickupLocation: 'Pickup location',
      notifications: "Notifications",
      bySMS: 'SMS',
      byEmail: 'Email',
      onDeliveryDay: "On delivery day",
      subscribedToEmails: "Receive order information emails",
      formInvalid: "The form contains errors. Please fix them before saving.",
    });
    return {
      provinces: Provinces.getForDropdown(),
      sms_preference: [],
      member: {},
      status: [
        "disabled", "member", "admin"
      ],
      Rules: Rules,
      isSubmitLoading: false,
      showPassword: false,
      formInvalidSnackbar: false,
    }
  },
  mounted: function () {
    this.resetMember();
  },
  methods: {
    getProvinceText: function (item) {
      const text = this.$t('province:' + item.value);
      return text === "undefined" ? "" : text;
    },
    submit: async function () {
      const formValidation = await this.isValid();
      this.formInvalidSnackbar = false;
      if (!formValidation.valid) {
        this.formInvalidSnackbar = true;
        this.$refs.memberForm.scrollIntoView({ behavior: 'smooth' })
        return;
      }
      this.isSubmitLoading = true;
      await this.$emit('submitted', this.member, () => {
        this.isSubmitLoading = false;
      });
    },
    setMember: function (member) {
      this.member = {
        ...member,
        locale: member.locale ?? this.$store.state.locale
      }
    },
    resetMember: function () {
      this.member = {
        emailOnDeliveryDay: true,
        sms_preference: ['beforeDistribution'],
        locale: this.$store.state.locale
      }
    },
    isValid: async function () {
      await this.$nextTick();
      return this.$refs.memberForm.validate();
    }
  },
  computed: {
    isCreateFlow: function () {
      return this.member.id === undefined;
    },
    pickupLocations: function () {
      return this.$store.state.pickupLocations;
    },
    pickupLocationId: {
      get() {
        return this.pickupLocations.some(
          location => location.id === this.member.PickupLocationId
        )
          ? this.member.PickupLocationId
          : null
      },
      set(value) {
        this.member.PickupLocationId = value
      }
    }
  }
}
</script>

<style scoped></style>
